import { createEvent, getDynamicData } from '@avensia/excite';
import * as React from 'react';
import { cartUpdateEvent } from '@avensia/excite-commercetools';
import { type CommandType } from '@avensia/excite-commercetools/cart/command';
import {
  cartToCartItems,
  cartToDataLayerInfo,
  cartToEcommerceItems,
  getLineItemTrackingInfo,
  getCheckoutPageUrl,
} from '../utils';
import { KlarnaCheckoutApiEventName, type KlarnaCheckoutApiEventPayload } from '@avensia/excite-klarna-commercetools';
import { type ChangedDataType } from '@avensia/excite-ingrid/types';
import { addCheckoutNinetailedEvent } from 'webapp/ninetailed/tracking';
import { addToDataLayer, type DataLayerEvent } from '..';

export function useCheckoutTracking() {
  usePaymentInfoTracking();
  useShippingInfoTracking();
  useKcoEventTracking();
}

export type PaymentInfoEvent = {
  type: string;
  cart?: Excite.Commercetools.CartApiModel;
};

export const [paymentInfoEvent] = createEvent<PaymentInfoEvent>();

export type KcoEvent = {
  [K in KlarnaCheckoutApiEventName]: { name: K; value: KlarnaCheckoutApiEventPayload[K] };
}[KlarnaCheckoutApiEventName];

export const [kcoEvent] = createEvent<KcoEvent>();

export function onKcoEvent(event: KlarnaCheckoutApiEventName, arg: KlarnaCheckoutApiEventPayload[typeof event]) {
  kcoEvent.emit({ name: event, value: arg as any });
}

export function buildEventListenerObject() {
  return (
    Object.entries(KlarnaCheckoutApiEventName) as Array<
      [keyof typeof KlarnaCheckoutApiEventName, KlarnaCheckoutApiEventName]
    >
  ).reduce(
    (res, [key, value]) => {
      return {
        ...res,
        [key]: function (arg: KlarnaCheckoutApiEventPayload[typeof value]) {
          onKcoEvent(KlarnaCheckoutApiEventName[key], arg);
        },
      };
    },
    {} as {
      [event in keyof typeof KlarnaCheckoutApiEventName]: (
        args: KlarnaCheckoutApiEventPayload[(typeof KlarnaCheckoutApiEventName)[event]],
      ) => void;
    },
  );
}

function usePaymentInfoTracking() {
  React.useEffect(
    () =>
      paymentInfoEvent.on(async data => {
        data.cart = await getDynamicData<Excite.Commercetools.CartApiModel>().current();
        const ecommerceItems = cartToEcommerceItems(data.cart);

        const ga4Event: DataLayerEvent = {
          event: 'add_payment_info',
          ecommerce: {
            payment_type: data.type,

            items: ecommerceItems ?? [],
          },
          _clear: ecommerceItems ? ecommerceItems.length > 0 : false,
        };
        addToDataLayer(ga4Event);
      }),
    [],
  );
}

export function isSetShippingMethodCommand(
  command: CommandType,
): command is
  | NordicNest.Api.NordicNestSetShippingMethodCommand
  | Excite.Commercetools.SetShippingMethodCommand
  | Excite.Commercetools.SetCustomShippingMethodCommand {
  return (
    command.$type === 'NordicNestSetShippingMethodCommand' ||
    command.$type === 'SetShippingMethodCommand' ||
    command.$type === 'SetCustomShippingMethodCommand'
  );
}
function useShippingInfoTracking() {
  React.useEffect(
    () =>
      cartUpdateEvent.on(async data => {
        if (isSetShippingMethodCommand(data.command)) {
          const ga4Event: DataLayerEvent = {
            event: 'add_shipping_info',
            ecommerce: {
              //Default values, should be overridden by additionalProductAttributes in the decorator
              selected_shipping_method: data.command.id,
              selected_shipping_price: data.cart?.shippingCost?.inclVat,
              ...(await cartToDataLayerInfo()),
              items: cartToEcommerceItems(data.cart) ?? [],
            },
          };
          addToDataLayer(ga4Event);
        }
      }),
    [],
  );
}

export async function onShippingSelected(data: ChangedDataType) {
  const cart = await getDynamicData<Excite.Commercetools.CartApiModel>().current();
  const ga4Event: DataLayerEvent = {
    event: 'add_shipping_info',
    ecommerce: {
      //Default values, should be overridden by additionalProductAttributes in the decorator
      selected_shipping_method: data.shipping_method,
      selected_shipping_price: data.price,
      ...(await cartToDataLayerInfo()),
      items: cartToEcommerceItems(cart) ?? [],
    },
  };
  addToDataLayer(ga4Event);
}

function useKcoEventTracking() {
  const kcoDatalayerEvent = React.useRef<Record<KlarnaCheckoutApiEventName, boolean>>(
    Object.values(KlarnaCheckoutApiEventName).reduce(
      (acc, curr) => ({ ...acc, [curr]: false }),
      {} as Record<KlarnaCheckoutApiEventName, boolean>,
    ),
  );
  const timer = React.useRef<NodeJS.Timeout>(undefined);
  React.useEffect(
    () =>
      kcoEvent.on(async data => {
        if (kcoDatalayerEvent.current[data.name]) {
          return;
        }
        kcoDatalayerEvent.current[data.name] = true;
        if (timer.current !== undefined) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(async () => {
          const ga4Event: DataLayerEvent = {
            event: 'kco',
            event_info: {
              ...Object.fromEntries(
                Object.entries(kcoDatalayerEvent.current).map(([key, value]) => ['kco_' + key, value]),
              ),
            },
          };
          addToDataLayer(ga4Event);
        }, 200);
      }),
    [],
  );
}

export function useShowCartEvent(isOpen: boolean) {
  React.useEffect(() => {
    if (isOpen) {
      (async () => {
        const { items, cart } = await getLineItemTrackingInfo();
        const cartItems = cartToCartItems(cart);
        const cartUrl = getCheckoutPageUrl();

        if (items && items.length > 0) {
          const ga4Event: DataLayerEvent = {
            event: 'view_cart',
            ecommerce: {
              ...(await cartToDataLayerInfo()),
              items,
            },
            cart_items: cartItems,
            cart_id: cart?.id,
            cart_url: cartUrl,
          };
          addToDataLayer(ga4Event);
        }
      })();
    }
  }, [isOpen]);
}

export async function addCheckoutEvent() {
  const { items, cart } = await getLineItemTrackingInfo();

  if (items && items.length > 0) {
    const ga4Event: DataLayerEvent = {
      event: 'begin_checkout',
      ecommerce: {
        // This is not a real hook but it cannot be used in initial script on server
        // eslint-disable-next-line react-hooks/rules-of-hooks
        ...(await cartToDataLayerInfo()),
        items,
      },
    };
    addToDataLayer(ga4Event);
    addCheckoutNinetailedEvent(ga4Event);
  }
}
